import { useMemo, useState } from 'react';
import { Building2, Network, Plus, MoreHorizontal, Search } from 'lucide-react';
import { PageHeader, Toolbar } from '@vnetwork/ui/layout';
import {
  DataTable,
  EmptyState,
  Pagination,
  StatusPill,
  Stat,
  StatGroup,
  type ColumnDef,
} from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Skeleton,
  SegmentedControl,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  IconButton,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useChildren,
  useOrgStats,
  useRemoveChild,
  orgErrorMessage,
  type ChildCustomer,
} from '@/api/organization';
import { statusTone, titleCase, formatDate, formatCurrency } from '../shared';
import { AddChildDialog } from './AddChildDialog';
import { ChildServicesTable, ChildInvoicesTable } from './ChildResourceTables';

const PAGE_SIZE = 20;
type View = 'customers' | 'services' | 'invoices';
const VIEWS = [
  { value: 'customers', label: 'Customers' },
  { value: 'services', label: 'Services' },
  { value: 'invoices', label: 'Invoices' },
];

interface SubCustomersTabProps {
  canManage: boolean;
}

export function SubCustomersTab({ canManage }: SubCustomersTabProps) {
  const { show } = useToast();
  const { data: stats, isLoading: statsLoading } = useOrgStats();
  const [view, setView] = useState<View>('customers');
  // Lifted so the "Link customer" primary action can live in the PageHeader
  // while the AddChildDialog stays with the customers table.
  const [addOpen, setAddOpen] = useState(false);

  return (
    <>
      <PageHeader
        icon={<Network size={22} />}
        title="Customers"
        description="Resold customers linked to your organization — their services, invoices and revenue."
        actions={
          canManage && view === 'customers' ? (
            <Button variant="primary" startIcon={<Plus size={15} />} onClick={() => setAddOpen(true)}>
              Link customer
            </Button>
          ) : undefined
        }
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <StatGroup columns={4}>
          <Stat
            label="Customers"
            value={statsLoading ? <Skeleton style={{ height: 28, width: 60 }} /> : String(stats?.customer_count ?? 0)}
          />
          <Stat
            label="Active services"
            value={statsLoading ? <Skeleton style={{ height: 28, width: 60 }} /> : String(stats?.active_services ?? 0)}
          />
          <Stat
            label="MRR"
            value={statsLoading ? <Skeleton style={{ height: 28, width: 80 }} /> : formatCurrency(stats?.mrr ?? 0)}
          />
          <Stat
            label="Pending revenue"
            value={statsLoading ? <Skeleton style={{ height: 28, width: 80 }} /> : formatCurrency(stats?.pending_revenue ?? 0)}
          />
        </StatGroup>

        <SegmentedControl
          aria-label="Customer view"
          items={VIEWS}
          value={view}
          onValueChange={(v) => setView(v as View)}
        />

        {view === 'customers' && (
          <ChildrenTable
            canManage={canManage}
            addOpen={addOpen}
            setAddOpen={setAddOpen}
            onError={(t) => show({ tone: 'danger', title: t })}
          />
        )}
        {view === 'services' && <ChildServicesTable />}
        {view === 'invoices' && <ChildInvoicesTable />}
      </div>
    </>
  );
}

interface ChildrenTableProps {
  canManage: boolean;
  addOpen: boolean;
  setAddOpen: (open: boolean) => void;
  onError: (title: string) => void;
}

function ChildrenTable({ canManage, addOpen, setAddOpen, onError }: ChildrenTableProps) {
  const { show } = useToast();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const { data, isLoading, isError } = useChildren({ page, pageSize: PAGE_SIZE });
  const [removing, setRemoving] = useState<ChildCustomer | null>(null);

  const remove = useRemoveChild({
    onSuccess: () => {
      show({ tone: 'success', title: 'Customer removed' });
      setRemoving(null);
    },
    onError: (err) => onError(orgErrorMessage(err, 'Could not remove customer')),
  });

  const pagination = data?.pagination;
  const allRows = data?.results ?? [];
  const q = search.trim().toLowerCase();
  const rows = q
    ? allRows.filter((c) =>
        [c.customer_name, c.customer_email, String(c.customer_id ?? ''), c.status].some((v) =>
          v?.toLowerCase().includes(q),
        ),
      )
    : allRows;

  const columns = useMemo<ColumnDef<ChildCustomer, unknown>[]>(() => {
    const cols: ColumnDef<ChildCustomer, unknown>[] = [
      {
        id: 'customer',
        header: 'Customer',
        cell: ({ row }) => (
          <span style={{ display: 'grid', lineHeight: 1.3 }}>
            <span style={{ fontWeight: 500 }}>{row.original.customer_name || '—'}</span>
            {row.original.customer_email && (
              <span style={{ color: 'var(--vn-fg-muted)', fontSize: 12 }}>{row.original.customer_email}</span>
            )}
          </span>
        ),
      },
      { id: 'id', header: 'Customer ID', cell: ({ row }) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.original.customer_id}</span> },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => (
          <StatusPill tone={statusTone(row.original.status)}>{titleCase(row.original.status || 'unknown')}</StatusPill>
        ),
      },
      { id: 'joined', header: 'Joined', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{formatDate(row.original.joined_at)}</span> },
    ];

    if (canManage) {
      cols.push({
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div style={{ textAlign: 'right' }}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton variant="ghost" size="sm" aria-label={`Actions for ${row.original.customer_name}`}>
                  <MoreHorizontal size={16} />
                </IconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem tone="danger" onSelect={() => setRemoving(row.original)}>
                  Remove customer
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      });
    }
    return cols;
  }, [canManage]);

  return (
    <div>
      <Toolbar
        start={
          <Input
            placeholder="Search customers…"
            startSlot={<Search size={15} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
        }
      />

      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 44 }} />
          ))}
        </div>
      ) : isError ? (
        <EmptyState icon={<Building2 size={28} />} title="Couldn't load customers" description="Something went wrong. Please try again." />
      ) : rows.length === 0 ? (
        q ? (
          <EmptyState icon={<Search size={28} />} title="No matching customers" description={`No customers match “${search.trim()}”.`} />
        ) : (
          <EmptyState
            icon={<Building2 size={28} />}
            title="No customers yet"
            description={canManage ? 'Link an existing customer to manage them here.' : 'No customers have been linked.'}
            actions={
              canManage ? (
                <Button startIcon={<Plus size={15} />} onClick={() => setAddOpen(true)}>
                  Link customer
                </Button>
              ) : undefined
            }
          />
        )
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => String(r.id)}
          footer={
            pagination && pagination.total_pages > 1 ? (
              <Pagination page={pagination.page} pageCount={pagination.total_pages} onPageChange={setPage} summary={`Showing ${rows.length} of ${pagination.total} customers`} />
            ) : (
              <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                {`${pagination?.total ?? rows.length} customer${(pagination?.total ?? rows.length) === 1 ? '' : 's'}`}
              </span>
            )
          }
        />
      )}

      <AddChildDialog open={addOpen} onOpenChange={setAddOpen} />
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title="Remove customer?"
        description={`“${removing?.customer_name ?? 'This customer'}” will no longer be linked to your organization.`}
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing.customer_id)}
      />
    </div>
  );
}
